import {
  QUESTION_TYPE_PRESET_MAP,
  type QuestionTypePreset,
  type QuestionBlock,
} from '@eps/shared-types';

// crypto.randomUUID needs a secure context (https/localhost); LAN http origins lack it.
export const genBlockId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/** Depth-first search for the first block of `type`, descending into container columns. */
function firstOfType<T extends QuestionBlock['type']>(
  blocks: QuestionBlock[],
  type: T,
): Extract<QuestionBlock, { type: T }> | undefined {
  for (const b of blocks) {
    if (b.type === type) return b as Extract<QuestionBlock, { type: T }>;
    if (b.type === 'container') {
      for (const col of b.columns) {
        const found = firstOfType(col.blocks, type);
        if (found) return found as Extract<QuestionBlock, { type: T }>;
      }
    }
  }
  return undefined;
}

/** First empty block found (depth-first), or null if every block has its content filled in. */
function findEmptyBlock(blocks: QuestionBlock[]): QuestionBlock | null {
  for (const b of blocks) {
    if (b.type === 'text' && !b.text.trim()) return b;
    if (b.type === 'image' && !b.imageUrl.trim()) return b;
    if (b.type === 'audio' && !b.audioUrl.trim()) return b;
    if (b.type === 'container') {
      const totalBlocks = b.columns.reduce((n, col) => n + col.blocks.length, 0);
      if (totalBlocks === 0) return b;
      for (const col of b.columns) {
        const empty = findEmptyBlock(col.blocks);
        if (empty) return empty;
      }
    }
  }
  return null;
}

function projectLegacyContent(blocks: QuestionBlock[]) {
  return {
    text: firstOfType(blocks, 'text')?.text ?? '',
    imageUrl: firstOfType(blocks, 'image')?.imageUrl ?? '',
    audioUrl: firstOfType(blocks, 'audio')?.audioUrl ?? '',
  };
}

export interface EditableQuestion {
  _id: string;
  name?: string;
  types?: string[];
  category?: string;
  subcategory?: string;
  difficulty?: string;
  questionLengthType?: string;
  tier?: string;
  groupId?: string;
  ubtLayout?: string;
  ubtPrompt?: { stem?: string };
  question?: {
    format?: string;
    text?: string;
    imageUrl?: string;
    audioUrl?: string;
    blocks?: QuestionBlock[];
  };
  options?: {
    format?: string;
    text?: string;
    imageUrl?: string;
    audioUrl?: string;
    isCorrect?: boolean;
  }[];
  explanation?: { en?: string };
  tags?: string[];
  publishStatus?: string;
  questionType?: string;
}

export const TYPES = ['ubt', 'quiz', 'cbt'] as const;
export const CATEGORIES = ['reading', 'listening'] as const;
export const READING_SUBCATEGORIES = [
  'vocab_grammar',
  'visual',
  'comprehension',
  'context_clues',
  // Supplementary, not part of the graded EPS-TOPIK spec — see
  // QuestionSubcategory.CULTURE in @eps/shared-types.
  'culture',
] as const;
export const LISTENING_SUBCATEGORIES = ['picture', 'dialogue', 'response', 'extended'] as const;
export const SUBCATEGORY_LABELS: Record<string, string> = {
  vocab_grammar: 'Vocab & Grammar (fill-blank)',
  visual: 'Visual (sign, graph, price tag)',
  comprehension: 'Reading Comprehension',
  context_clues: 'Context Clues',
  picture: 'Picture / Illustration',
  dialogue: 'Dialogue / Monologue',
  response: 'Response / Action',
  extended: 'Extended Listening',
  culture: 'Korean Culture (supplementary)',
};
export const DIFFICULTIES = ['beginner', 'intermediate', 'advanced'] as const;
export const QUESTION_LENGTH_TYPES = ['short', 'long'] as const;
export const TIERS = ['free', 'pro_medium'] as const;
// Only these two produce distinct rendering in the real exam (see
// apps/mobile MockTestScreen's getDerivedQuestionHeading). 'numbered_stem'
// is a legacy enum value with no distinct behavior — kept in shared-types
// for backward compat with existing data, not offered as a new choice.
export const UBT_LAYOUTS = ['set_with_stem', 'number_only'] as const;
export const UBT_LAYOUT_LABELS: Record<string, string> = {
  set_with_stem: 'Own prompt (this question has its own text)',
  numbered_stem: 'Numbered + stem (number then group prompt)',
  number_only: 'Number only (relies on group prompt)',
};

export const genGroupId = () =>
  `g_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

export interface OptionForm {
  format: string;
  text: string;
  imageUrl: string;
  audioUrl: string;
  isCorrect: boolean;
}
export interface QuestionForm {
  name: string;
  types: string[];
  category: string;
  subcategory: string;
  difficulty: string;
  questionLengthType: string;
  tier: string;
  groupId: string;
  ubtLayout: string;
  ubtPrompt: {
    stem: string;
  };
  question: {
    format: string;
    text: string;
    imageUrl: string;
    audioUrl: string;
    blocks: QuestionBlock[];
  };
  options: OptionForm[];
  explanation: { en: string };
  tags: string;
  publishImmediately: boolean;
  /** Explicitly admin-chosen preset; null if never chosen or invalidated by a manual edit. */
  questionType: QuestionTypePreset | null;
}

export const emptyOption = (): OptionForm => ({
  format: 'text',
  text: '',
  imageUrl: '',
  audioUrl: '',
  isCorrect: false,
});
/**
 * Reset for "create another" — clears per-question content (name, blocks,
 * options, explanation, tags) but carries over the classification settings
 * from the just-created question (type, category, group, layout, ...), so
 * batch-adding siblings to the same group/type doesn't require reselecting
 * them every time.
 */
export const nextForm = (prev: QuestionForm): QuestionForm => ({
  ...emptyForm(),
  types: prev.types,
  category: prev.category,
  subcategory: prev.subcategory,
  difficulty: prev.difficulty,
  questionLengthType: prev.questionLengthType,
  tier: prev.tier,
  groupId: prev.groupId,
  ubtLayout: prev.ubtLayout,
  ubtPrompt: prev.ubtPrompt,
  publishImmediately: prev.publishImmediately,
});

export const emptyForm = (): QuestionForm => ({
  name: '',
  types: ['ubt'],
  category: 'reading',
  subcategory: '',
  difficulty: 'beginner',
  questionLengthType: 'short',
  tier: 'free',
  groupId: '',
  ubtLayout: 'set_with_stem',
  ubtPrompt: { stem: '' },
  question: { format: 'text', text: '', imageUrl: '', audioUrl: '', blocks: [] },
  options: [emptyOption(), emptyOption(), emptyOption(), emptyOption()],
  explanation: { en: '' },
  tags: '',
  publishImmediately: true,
  questionType: null,
});

/**
 * question.format is derived from content, not admin-set: nothing outside
 * this admin form reads it as authoritative (confirmed via repo-wide grep),
 * so "richest content wins" (audio > image > text) keeps it a sane single
 * value for anything that still displays it (e.g. a list-view icon).
 */
function deriveStemFormat(question: { imageUrl: string; audioUrl: string }): string {
  if (question.audioUrl) return 'audio';
  if (question.imageUrl) return 'image';
  return 'text';
}

/**
 * Mirrors the server-side `questionType` validator in
 * `apps/server/src/modules/questions/question.model.ts` exactly: the server
 * checks the declared preset against *actual content presence*, while the
 * admin form tracks it against *declared toggles*. Those diverge whenever a
 * preset declares a field the admin never filled in — e.g. picking
 * "Audio Q → Text A" and leaving the (always-optional) question text blank,
 * or picking any text-stem preset while UBT layout "number_only" hides the
 * stem text input entirely. Emitting `questionType` in those cases produces a
 * payload the server deterministically rejects with a generic 500.
 *
 * So the payload boundary is the single place that decides: emit the type tag
 * only when it actually holds. A mismatch silently drops the tag (the question
 * still saves, just untagged) rather than failing the save.
 *
 * Deliberately NOT implemented as `questionType === inferPresetFromQuestion(...).presetId`:
 * several presets share one content shape (FILL_BLANK_TEXT and PARAGRAPH_TEXT are
 * identical; AUDIO_TEXT/AUDIO_IMAGE share a stem), and `inferPresetFromQuestion`
 * resolves ties by returning the first match. Equality against it would silently
 * drop a correctly-filled PARAGRAPH_TEXT because inference always answers
 * FILL_BLANK_TEXT for that shape. Checking the *declared* preset's own shape —
 * exactly what the server does — accepts every preset the server would accept.
 *
 * Reads `f.question.blocks` (via `projectLegacyContent`), not the legacy
 * fields directly — those are now write-only-at-save, derived from blocks,
 * so blocks is the only live source of truth once a question is edited.
 */
function questionTypeMatchesContent(f: QuestionForm): boolean {
  if (!f.questionType) return false;
  const preset = QUESTION_TYPE_PRESET_MAP[f.questionType];
  if (!preset) return false;
  const projected = projectLegacyContent(f.question.blocks);
  const stemMatches =
    preset.stem.text === !!projected.text &&
    preset.stem.image === !!projected.imageUrl &&
    preset.stem.audio === !!projected.audioUrl;
  const optionsMatch = f.options.every((o) => o.format === preset.optionFormat);
  return stemMatches && optionsMatch;
}

/**
 * Every present block's own field must be non-empty, all the way down into
 * container columns; a container needs at least one block somewhere across
 * its columns. Mirrors the old stemValidationError's UBT "number only" rule:
 * an empty block list is accepted there, since the group prompt supplies the
 * stem instead. Also accepted for any *other* layout once a group prompt is
 * actually attached (groupId + ubtPrompt.stem) — mobile always renders the
 * group's shared prompt above every sibling question regardless of
 * ubtLayout (see MockTestScreen's getDerivedGroupPrompt), so a question
 * whose group already supplies the stem is genuinely content-complete with
 * an empty stem of its own; only the number needs to show.
 */
export function blocksValidationError(f: QuestionForm): string | null {
  const groupPromptSuffices = f.ubtLayout === 'number_only' || !!f.ubtPrompt.stem.trim();
  if (f.question.blocks.length === 0) {
    return groupPromptSuffices
      ? null
      : 'Add at least one block (text, image, audio, or container) to the question.';
  }
  const empty = findEmptyBlock(f.question.blocks);
  if (!empty) return null;
  return empty.type === 'container'
    ? 'Add at least one text or image block inside the container.'
    : `Fill in the ${empty.type} block, or remove it.`;
}

export function toPayload(f: QuestionForm) {
  const projected = projectLegacyContent(f.question.blocks);
  return {
    ...(f.name && { name: f.name.trim() }),
    types: f.types,
    category: f.category,
    ...(f.subcategory && { subcategory: f.subcategory }),
    difficulty: f.difficulty,
    ...(f.questionLengthType && { questionLengthType: f.questionLengthType }),
    tier: f.tier,
    ...(f.groupId && { groupId: f.groupId.trim() }),
    ...(f.types.includes('ubt') && {
      ...(f.ubtLayout && { ubtLayout: f.ubtLayout }),
      ubtPrompt: {
        ...(f.ubtPrompt.stem && { stem: f.ubtPrompt.stem.trim() }),
      },
    }),
    ...(questionTypeMatchesContent(f) && { questionType: f.questionType }),
    question: {
      format: deriveStemFormat(projected),
      ...(projected.text && { text: projected.text }),
      ...(projected.imageUrl && { imageUrl: projected.imageUrl }),
      ...(projected.audioUrl && { audioUrl: projected.audioUrl }),
      ...(f.question.blocks.length > 0 && { blocks: f.question.blocks }),
    },
    options: f.options.map((o) => ({
      format: o.format,
      ...(o.text && { text: o.text }),
      ...(o.imageUrl && { imageUrl: o.imageUrl }),
      ...(o.audioUrl && { audioUrl: o.audioUrl }),
      isCorrect: o.isCorrect,
    })),
    ...(f.explanation.en && { explanation: { en: f.explanation.en } }),
    ...(f.tags && {
      tags: f.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    }),
    ...(f.publishImmediately && { publishStatus: 'published' }),
  };
}
